import { tr } from 'mmfintech-commons'
import { formatCurrencyForDisplay } from '@utils'
import cn from 'classnames'
import type {
  BankAccountResponse,
  ExternalCryptoAddressResponse,
  ExternalWalletAccountResponse,
  RecipientResponse
} from 'mmfintech-backend-api/types'
import InfoRow from './InfoRow'

import './expenseRecipientDetails.scss'

type RecipientDetailsProps = {
  recipient: BankAccountResponse | ExternalCryptoAddressResponse | ExternalWalletAccountResponse | RecipientResponse
  inline?: boolean
}

const ExpenseRecipientDetails = ({ recipient, inline }: RecipientDetailsProps) => {
  return recipient ? (
    <div className={cn('recipient-details-wrapper', { inline })}>
      {!inline && <div className='heading'>{tr('FRONTEND.RECIPIENTS.EDIT.TITLE', 'Recipient details')}</div>}
      <div className='content'>
        {recipient.type?.includes('_BANK_ACCOUNT') && (
          <>
            <InfoRow
              label={tr('CARDS.ISSUING.CONFIRMATION.CARD_TYPE', 'Type')}
              value={
                recipient.personal
                  ? tr('FRONTEND.RECIPIENTS.PERSONAL_BANK_ACCOUNT', 'Bank account – personal')
                  : tr('FRONTEND.RECIPIENTS.TYPE.BANK_ACCOUNT', 'Bank account')
              }
              data-test='type'
            />
            <InfoRow
              label={tr('FRONTEND.COUNTERPARTY.COLUMN_NAME', 'Name')}
              value={(recipient as BankAccountResponse).holderName}
              data-test='holder-name'
            />
            <InfoRow
              label={tr('BANK_METADATA.LABELS.IBAN', 'IBAN')}
              value={(recipient as BankAccountResponse).iban}
              data-test='iban'
            />
            <InfoRow
              label={tr('FRONTEND.DEPOSIT.PREVIEW.BIC_SWIFT', 'BIC/SWIFT')}
              value={(recipient as BankAccountResponse).bicSwift}
              data-test='bic-swift'
            />
            <InfoRow
              label={tr('FRONTEND.RECIPIENTS.LABELS.BANK_CODE', 'Bank code')}
              value={(recipient as BankAccountResponse).bankCode}
              data-test='bank-code'
            />
            <InfoRow
              label={tr('FRONTEND.DEPOSIT.PREVIEW.BANK_NAME', 'Bank name')}
              value={(recipient as BankAccountResponse).bankName}
              data-test='bank-name'
            />
            <InfoRow
              label={tr('FRONTEND.RECIPIENTS.PIX.BRANCH_CODE', 'Branch code')}
              value={(recipient as BankAccountResponse).branchCode}
              data-test='branch-code'
            />
            <InfoRow
              label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.BRANCH_NAME', 'Branch name')}
              value={(recipient as BankAccountResponse).branchName}
              data-test='branch-name'
            />
            <InfoRow
              label={tr('FRONTEND.RECIPIENTS.PIX.ACCOUNT_NUMBER', 'Account number')}
              value={(recipient as BankAccountResponse).accountNumber}
              data-test='account-number'
            />
          </>
        )}

        {recipient.type?.includes('_CRYPTO_ADDRESS') && (
          <>
            <InfoRow
              label={tr('CARDS.ISSUING.CONFIRMATION.CARD_TYPE', 'Type')}
              value={
                recipient.personal
                  ? tr('FRONTEND.RECIPIENTS.PERSONAL_CRYPTO', 'Crypto – personal')
                  : tr('FRONTEND.HEADER.CRYPTO', 'Crypto')
              }
              data-test='type'
            />
            <InfoRow
              label={tr('FRONTEND.COUNTERPARTY.COLUMN_NAME', 'Name')}
              value={(recipient as ExternalCryptoAddressResponse).name}
              data-test='holder-name'
            />
            <InfoRow
              label={tr('CARDS.ISSUING.CONFIRMATION.CHECK_ADDRESS_LABEL', 'Address')}
              value={(recipient as ExternalCryptoAddressResponse).address}
              data-test='crypto-address'
            />
            <InfoRow
              label={tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.TAG', 'Tag')}
              value={(recipient as ExternalCryptoAddressResponse).tag}
              data-test='crypto-tag'
            />
            <InfoRow
              label={tr('FRONTEND.ACCOUNTS.CREATE.CURRENCY_LABEL', 'Currency')}
              value={formatCurrencyForDisplay(recipient.currency)}
              data-test='currency'
            />
          </>
        )}

        {recipient.type?.includes('_WALLET_ACCOUNT') && (
          <>
            <InfoRow
              label={tr('CARDS.ISSUING.CONFIRMATION.CARD_TYPE', 'Type')}
              value={tr('FRONTEND.RECIPIENTS.KINGDOM_WALLET', 'Kingdom Wallet')}
              data-test='type'
            />
            <InfoRow
              label={tr('FRONTEND.COUNTERPARTY.COLUMN_NAME', 'Name')}
              value={(recipient as ExternalWalletAccountResponse).name}
              data-test='holder-name'
            />
            <InfoRow
              label={tr('PORTAL.FORGOT_PASSWORD.EMAIL', 'Email')}
              value={(recipient as ExternalWalletAccountResponse).email}
              data-test='holder-email'
            />
            <InfoRow
              label={tr('FRONTEND.CUSTOMERS.EDIT.PHONE', 'Phone')}
              value={(recipient as ExternalWalletAccountResponse).phone}
              data-test='holder-phone'
            />
            <InfoRow
              label={tr('FRONTEND.ACCOUNTS.CREATE.CURRENCY_LABEL', 'Currency')}
              value={formatCurrencyForDisplay(recipient.currency)}
              data-test='currency'
            />
          </>
        )}

        <InfoRow
          label={tr('FRONTEND.RECIPIENTS.LABELS.DESCRIPTION', 'Description')}
          value={recipient.description}
          data-test='description'
        />
      </div>
    </div>
  ) : null
}

export default ExpenseRecipientDetails
